"use client";

import { createContext, useContext, useEffect, useState } from "react";

export interface CartItem {
  id: number;
  slug: string;
  title: string;
  image: string | null;
  price: number;
  modality?: "ead" | "presencial";
}

interface CartContextValue {
  items: CartItem[];
  count: number;
  total: number;
  add: (item: CartItem) => void;
  remove: (id: number, modality?: string) => void;
  clear: () => void;
  has: (id: number, modality?: string) => boolean;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);
const STORAGE_KEY = "ctseg_cart";

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // Carrega do localStorage ao montar
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {
      // ignora corrupção
    }
    setHydrated(true);
  }, []);

  // Persiste ao mudar (sem disparar evento custom — evita loop)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items, hydrated]);

  // Sincroniza entre ABAS DIFERENTES (evento 'storage' nativo só dispara
  // em outras abas — nunca na que fez a mudança, então não há loop)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      try {
        setItems(e.newValue ? JSON.parse(e.newValue) : []);
      } catch {}
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const itemKey = (id: number, modality?: string) => `${id}-${modality || "default"}`;

  const add = (item: CartItem) => {
    setItems((prev) => {
      // Não adiciona duplicado (mesma combinação id + modalidade)
      const key = itemKey(item.id, item.modality);
      if (prev.some((i) => itemKey(i.id, i.modality) === key)) return prev;
      return [...prev, item];
    });
  };

  const remove = (id: number, modality?: string) => {
    setItems((prev) => prev.filter((i) => itemKey(i.id, i.modality) !== itemKey(id, modality)));
  };

  const clear = () => setItems([]);

  const has = (id: number, modality?: string) =>
    items.some((i) => itemKey(i.id, i.modality) === itemKey(id, modality));

  const count = items.length;
  const total = items.reduce((sum, i) => sum + (Number(i.price) || 0), 0);

  return (
    <CartContext.Provider value={{ items, count, total, add, remove, clear, has }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart deve ser usado dentro de CartProvider");
  return ctx;
}
